import { useMemo, useEffect, useRef, useCallback } from 'react';
import { GET_BASE_VIEWER_DATA } from 'modules/Apollo/queries';
import useWorklistId from 'hooks/useWorklistId';
import { useRegisterStudyColors } from 'hooks/useStudyColor';
import type { Study, StudyGroup } from 'domains/viewer/ViewportsConfigurations/types';
import type {
  GetBaseViewerDataQuery,
  GetBaseViewerDataQueryVariables,
  SeriesClassificationSummary,
} from 'generated/graphql';
import { seriesToSlimSeries, stackToSlimStack } from '../../ViewportsConfigurations/manipulators';
import type { ApolloQueryResult } from '@apollo/client';
import { NetworkStatus } from '@apollo/client';
import { emitPrecacheStatus } from 'modules/Apollo/precache/emitPrecacheStatus';
import { usePrecacheQuery } from 'modules/Apollo/hooks/usePrecacheQuery';
import { useIsViewerMetadataPrecacheEnabled } from './useIsViewerMetadataPrecacheEnabled';
import { deletePrecacheQuery } from 'modules/Apollo/precache/precacheOperation';
import { logger } from 'modules/logger';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags/suspense';
import { useExcludedAutoloadedPriorSmids, usePriorStudiesParams } from 'hooks/studyParams';
import { GraphQLError } from 'graphql';
import { WithApolloContext } from 'modules/Apollo/types';

export const useStudyColorTracker = ({
  loadedStudies,
  pendingStudies,
}: {
  loadedStudies: string[];
  pendingStudies: string[];
}): void => {
  const registerStudyColors = useRegisterStudyColors();

  useEffect(() => {
    registerStudyColors(loadedStudies, pendingStudies);
  }, [loadedStudies, pendingStudies, registerStudyColors]);
};

type BaseViewerDataHook = {
  studies: ReadonlyArray<Study>;
  mainStudiesIds: ReadonlyArray<string>;
  priorStudiesIds: ReadonlyArray<string>;
  autoloadedStudiesIds: ReadonlyArray<string>;
  loading: boolean;
  notFound: boolean;
  classifications: SeriesClassificationSummary[];
  smids?: Array<string>;
  refetch: () => Promise<ApolloQueryResult<GetBaseViewerDataQuery>>;
  hangingProtocol?: GetBaseViewerDataQuery['matchHangingProtocolByWorklistsAndStudies'];
  networkStatus: NetworkStatus;
  invalidatePrecacheForCurrentStudy: () => Promise<void>;
  invalidateAllPrecachedStudies: () => Promise<void>;
};

const usePriorSmidsFromUrl = (): string[] => {
  const priorStudies = usePriorStudiesParams();

  const smids = useMemo(() => {
    return priorStudies?.split(',') ?? [];
  }, [priorStudies]);

  return smids;
};

export type UseBaseViewerDataProps = {
  shouldEmitPrecacheStatus?: boolean;
} & WithApolloContext;

export const useBaseViewerData = ({
  shouldEmitPrecacheStatus = false,
  context,
}: UseBaseViewerDataProps): BaseViewerDataHook => {
  const urlPriorSmids = usePriorSmidsFromUrl();
  const excludedAutoloadedPriorSmids = useExcludedAutoloadedPriorSmids();
  const worklistId = useWorklistId();
  const emittedPrecacheStatus = useRef(false);
  const hasWorklistIds = worklistId != null;

  const shouldAutoloadPriors = useFeatureFlagEnabled(FF.AUTOLOAD_PRIORS);

  const queryVariables = useMemo(
    () => ({
      worklistIds: [worklistId].filter((worklistId): worklistId is string => Boolean(worklistId)),
      studyIds: urlPriorSmids,
      hasWorklistIds,
      hasStudyIds: urlPriorSmids.length > 0,
      includeAutoloadedPriors: shouldAutoloadPriors,
      skippedStudyIds: excludedAutoloadedPriorSmids,
    }),
    [urlPriorSmids, worklistId, hasWorklistIds, shouldAutoloadPriors, excludedAutoloadedPriorSmids]
  );

  const precacheEnabled = useIsViewerMetadataPrecacheEnabled();

  const { loading, data, previousData, refetch, error, networkStatus } = usePrecacheQuery<
    GetBaseViewerDataQuery,
    GetBaseViewerDataQueryVariables
  >(
    GET_BASE_VIEWER_DATA,
    {
      variables: queryVariables,
      skip: !hasWorklistIds,
      notifyOnNetworkStatusChange: true,
      context: {
        ...context,
        telemetry: { ...context?.telemetry, domain: 'precache' },
      },
    },
    {
      precacheEnabled,
    }
  );

  // We use the previous data to avoid flickering when the query is loading
  let latestPayload = data;

  if (latestPayload == null && previousData?.worklistItems != null) {
    const hasMatches = previousData?.worklistItems?.items.some(
      (worklist) => worklist.smid === worklistId
    );

    if (hasMatches) {
      latestPayload = previousData;
    } else {
      emittedPrecacheStatus.current = false;
    }
  }

  useEffect(() => {
    if (!shouldEmitPrecacheStatus || emittedPrecacheStatus.current) return;
    emitPrecacheStatus(GET_BASE_VIEWER_DATA, queryVariables);
    emittedPrecacheStatus.current = true;
  }, [queryVariables, shouldEmitPrecacheStatus]);

  const mainStudiesIds = useMemo(
    () =>
      latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.studies)
        .map((study) => study.smid) ?? [],
    [latestPayload]
  );

  const priorStudiesIds = useMemo(
    () => [
      ...(latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.autoloadedPriors ?? [])
        .map((study) => study.smid) ?? []),
      ...(latestPayload?.studiesBySmid?.map((study) => study.smid) ?? []),
    ],
    [latestPayload]
  );

  // lists the autoloaded priors available in the case, even if they've been skipped
  // this exists as a stable, reliable way to handle re-adding priors that were skipped
  // without having to check the url to see if that prior's smid is in the skipped params
  const autoloadedStudiesIds = useMemo(
    () => [
      ...(latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.allAutoloadedPriors ?? [])
        .map((study) => study.smid) ?? []),
    ],
    [latestPayload]
  );

  const studies = useMemo(() => {
    const mainStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.studies) ?? [];

    const autoloadedPriors =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.autoloadedPriors) ?? [];

    const comparativeStudies = latestPayload?.studiesBySmid ?? [];

    return [...mainStudies, ...autoloadedPriors, ...comparativeStudies].filter(
      (study): study is Study => Boolean(study)
    );
  }, [latestPayload]);

  const smids = useMemo(() => studies.map((study) => study.smid), [studies]);

  const invalidatePrecacheForCurrentStudy = useCallback(async () => {
    logger.info('Deleting precache query GET_BASE_VIEWER_DATA with variables:', queryVariables);
    await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA, variables: queryVariables });
  }, [queryVariables]);

  // This is a failsafe mechanism to delete the cache if the query returns no studies
  // If no studies are returned it's VERY likely something in our cache is wrong and
  // it does no harm to delete it for this query since it's theoretically empty anyway
  useEffect(() => {
    if (!loading && studies.length === 0 && worklistId != null) {
      invalidatePrecacheForCurrentStudy();
    }
  }, [loading, studies.length, worklistId, invalidatePrecacheForCurrentStudy]);

  const unableToFindStudyError =
    // @ts-expect-error [EN-10263] - TS2345 - Argument of type '(err: GraphQLError) => boolean' is not assignable to parameter of type '(value: GraphQLFormattedError, index: number, array: readonly GraphQLFormattedError[]) => unknown'.
    error?.graphQLErrors?.some((err: GraphQLError) => err.extensions.code === 404) ?? false;

  const notFound = !loading && (unableToFindStudyError || studies.length === 0);

  const hangingProtocol = latestPayload?.matchHangingProtocolByWorklistsAndStudies;

  // Cached HPs can become stale when HP are created/updated/deleted,
  // so we want to invalidate the cache for GBVD(HPs) when that happens.
  const invalidateAllPrecachedStudies = useCallback(async () => {
    await deletePrecacheQuery({ query: GET_BASE_VIEWER_DATA });
  }, []);

  const classifications = useMemo(() => {
    const mainStudies =
      latestPayload?.worklistItems?.items.flatMap((worklist) => worklist.studies) ?? [];

    const autoloadedPriorStudies =
      latestPayload?.worklistItems?.items
        .flatMap((worklist) => worklist.autoloadedPriors)
        .filter(Boolean) ?? [];

    const manualPriorStudies = latestPayload?.studiesBySmid ?? [];

    const series = [...mainStudies, ...autoloadedPriorStudies, ...manualPriorStudies].flatMap(
      (study) => study?.seriesList
    );

    return series
      .map((series) => (series?.classifications != null ? series.classifications[0] : null))
      .filter(
        (classification): classification is SeriesClassificationSummary => classification != null
      );
  }, [latestPayload]);

  return {
    smids,
    studies,
    mainStudiesIds,
    priorStudiesIds,
    autoloadedStudiesIds,
    loading,
    notFound,
    refetch,
    hangingProtocol,
    classifications,
    networkStatus,
    invalidatePrecacheForCurrentStudy,
    invalidateAllPrecachedStudies,
  };
};

export type StudiesHookResult = {
  groups: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
} & BaseViewerDataHook;
export const useStudies = ({ context }: WithApolloContext): StudiesHookResult => {
  const {
    smids = [],
    studies,
    mainStudiesIds,
    priorStudiesIds,
    autoloadedStudiesIds,
    ...studiesHook
  } = useBaseViewerData({ context });
  const { groups, groupedStudies } = useMemo(
    () => groupStudies({ smids, studies, mainStudiesIds }),
    [smids, studies, mainStudiesIds]
  );

  return {
    groups,
    groupedStudies,
    studies,
    mainStudiesIds,
    priorStudiesIds,
    autoloadedStudiesIds,
    ...studiesHook,
  };
};

export function groupStudies({
  smids,
  studies,
  mainStudiesIds,
}: {
  smids: readonly string[];
  studies: readonly Study[];
  mainStudiesIds: readonly string[];
}): {
  groups: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
} {
  const currentCaseStudies = studies.filter((study) => mainStudiesIds.includes(study.smid));
  const primaryStudySmids = currentCaseStudies.map((study) => study.smid);

  const groups = [
    primaryStudySmids,
    ...smids.filter((smid) => !primaryStudySmids.includes(smid)).map((smid) => [smid]),
  ].filter((group) => group.length > 0);

  const groupedStudies = groups.map((group) =>
    group.reduce<StudyGroup>((acc, studySmid) => {
      const study = studies.find((study) => study.smid === studySmid);
      if (study == null) return acc;
      return [
        ...acc,
        {
          smid: study.smid,
          description: study.description,
          seriesList: study.seriesList.map((series) => seriesToSlimSeries(series)),
          stackedFrames: study.stackedFrames.map((stack) => stackToSlimStack(stack)),
        },
      ];
    }, [])
  );

  return { groups, groupedStudies };
}
